import React from 'react';
import { Box, Typography, Paper, Chip, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeStyles } from '@material-ui/core/styles';
import { LoanSummaryData } from './types';

import { StatusCategory } from './types';

const categoryColors: Record<StatusCategory, { bg: string; color: string }> = {
  success: { bg: '#E8F5E9', color: '#2E7D32' },
  info:    { bg: '#E3F2FD', color: '#1565C0' },
  warning: { bg: '#FFF8E1', color: '#F57F17' },
  neutral: { bg: '#ECEFF1', color: '#546E7A' },
};

const getStatusStyle = (category?: StatusCategory) => {
  return categoryColors[category] || categoryColors.neutral;
};

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  loanId: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  statusChip: {
    height: '24px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  borrowerName: {
    fontSize: '15px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(0.5, 0),
  },
  label: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  value: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    fontWeight: 600,
    textAlign: 'right',
  },
  tasksSection: {
    marginTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
  },
  tasksLabel: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  taskList: {
    padding: 0,
  },
  taskItem: {
    padding: theme.spacing(0.25, 0),
  },
  taskIcon: {
    minWidth: '28px',
    color: theme.palette.warning.main,
  },
  taskText: {
    '& .MuiListItemText-primary': {
      fontSize: '13px',
    },
  },
  completedText: {
    fontSize: '13px',
    color: theme.palette.success.main,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
}));

const formatCurrency = (amount: string) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
};

interface LoanSummaryCardProps {
  data: LoanSummaryData;
}

export const LoanSummaryCard: React.FC<LoanSummaryCardProps> = ({ data }) => {
  const classes = useStyles();
  const statusStyle = getStatusStyle(data.statusCategory);
  const addresses = Array.isArray(data.propertyAddress)
    ? data.propertyAddress
    : [data.propertyAddress];

  return (
    <Paper elevation={0} className={classes.card}>
      <Box className={classes.header}>
        <Typography className={classes.loanId}>Loan #{data.loanId}</Typography>
        <Chip
          label={data.loanStatus}
          className={classes.statusChip}
          style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
          size="small"
        />
      </Box>
      <Typography className={classes.borrowerName}>{data.borrowerName}</Typography>
      <Box className={classes.row}>
        <Typography className={classes.label}>Loan Amount</Typography>
        <Typography className={classes.value}>{formatCurrency(data.loanAmount)}</Typography>
      </Box>
      <Box className={classes.row}>
        <Typography className={classes.label}>
          {addresses.length > 1 ? 'Properties' : 'Property'}
        </Typography>
        <Box>
          {addresses.map((addr, i) => (
            <Typography key={i} className={classes.value}>{addr}</Typography>
          ))}
        </Box>
      </Box>
      <Box className={classes.tasksSection}>
        <Typography className={classes.tasksLabel}>Pending Tasks</Typography>
        {data.pendingTasks?.length > 0 ? (
          <List className={classes.taskList}>
            {data.pendingTasks.map((task) => (
              <ListItem key={task.id} className={classes.taskItem} disableGutters>
                <ListItemIcon className={classes.taskIcon}>
                  <RadioButtonUncheckedIcon style={{ fontSize: '16px' }} />
                </ListItemIcon>
                <ListItemText primary={task.label} className={classes.taskText} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography className={classes.completedText}>
            <CheckCircleIcon style={{ fontSize: '16px' }} />
            No Pending Tasks. Everything is complete
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
